import * as React from "react";

import { t } from "@impero-com/common/utils/i18n";
import { assertNever } from "@impero-com/common/utils/obj";

import { BaseFormDisplay } from "components/form/legacyFields/formDisplay";
import { RawMultiNumericInput } from "components/form/legacyFields/multiNumericInput";
import { RawMultiSelectInput } from "components/form/legacyFields/multiSelectInput";
import { ReviewerOffsetUnit } from "legacyTypes/control";

import * as theme from "./theme.css";

const DEFAULT_OFFSET = 5;
const DEFAULT_UNIT: ReviewerOffsetUnit = "Day";
const MAX_DAY_OFFSET = 999;
const MAX_HOUR_OFFSET = 23;

interface ReviewerAllocatedTime {
    offset: number;
    unit: ReviewerOffsetUnit;
}

interface ReadOnlyProps {
    label: string;
    reviewerAllocatedTimes: Array<ReviewerAllocatedTime>;
    toPerformLabel: string;
}

interface EditProps extends ReadOnlyProps {
    onChange: (newValue: ReviewerAllocatedTime) => void;
}

type MultiReviewerAllocatedTimeInputProps =
    | ({ type: "READ_ONLY" } & ReadOnlyProps)
    | ({ type: "EDIT" } & EditProps);

interface MultiReviewerAllocatedTimeInputState {
    offsets: Array<number>;
    units: Array<ReviewerOffsetUnit>;
    change: ReviewerAllocatedTime | undefined;
}

class MultiReviewerAllocatedTimeInput extends React.Component<
    MultiReviewerAllocatedTimeInputProps,
    MultiReviewerAllocatedTimeInputState
> {
    constructor(props: MultiReviewerAllocatedTimeInputProps) {
        super(props);
        this.state = {
            units: props.reviewerAllocatedTimes.map((at) => at.unit),
            offsets: props.reviewerAllocatedTimes.map((at) => at.offset),
            change: undefined,
        };
    }

    render(): React.ReactNode {
        const { label } = this.props;
        return (
            <BaseFormDisplay label={label}>
                {this.renderFields()}
            </BaseFormDisplay>
        );
    }

    private renderFields = (): React.ReactNode => {
        const { offsets, units } = this.state;
        const { props } = this;
        switch (props.type) {
            case "EDIT": {
                return (
                    <MultiOffsetAndUnit
                        type="EDIT"
                        offsets={offsets}
                        units={units}
                        toPerformLabel={props.toPerformLabel}
                        onChange={this.onOffsetAndUnitChange}
                    />
                );
            }
            case "READ_ONLY": {
                return (
                    <MultiOffsetAndUnit
                        type="READ_ONLY"
                        offsets={offsets}
                        units={units}
                        toPerformLabel={props.toPerformLabel}
                    />
                );
            }
            default: {
                return assertNever(props);
            }
        }
    };

    private onOffsetAndUnitChange = ({
        unit,
        offset,
    }: {
        unit: ReviewerOffsetUnit;
        offset: number;
    }) => {
        const newState: MultiReviewerAllocatedTimeInputState = {
            units: [unit],
            offsets: [offset],
            change: { offset, unit },
        };
        this.setState(newState, () => this.callOnChange());
    };

    private callOnChange = () => {
        const { props } = this;
        switch (props.type) {
            case "EDIT": {
                const { onChange } = props;
                const { change } = this.state;
                if (onChange !== undefined) {
                    if (change !== undefined) {
                        onChange(change);
                    } else {
                        throw new Error(
                            "Cannot call onChange() because change is undefined"
                        );
                    }
                }
                break;
            }
            case "READ_ONLY": {
                throw new Error("Attempt to call onChange for READ_ONLY type");
            }
            default: {
                assertNever(props);
            }
        }
    };
}

interface MultiOffsetAndUnitReadOnlyProps {
    units: Array<ReviewerOffsetUnit>;
    offsets: Array<number>;
    toPerformLabel: string;
}

interface MultiOffsetAndUnitEditProps extends MultiOffsetAndUnitReadOnlyProps {
    onChange: (changes: { unit: ReviewerOffsetUnit; offset: number }) => void;
}

type MultiOffsetAndUnitProps =
    | ({ type: "EDIT" } & MultiOffsetAndUnitEditProps)
    | ({ type: "READ_ONLY" } & MultiOffsetAndUnitReadOnlyProps);

interface MultiMultiOffsetAndUnitState {
    units: Array<ReviewerOffsetUnit>;
    offsets: Array<number>;
}

class MultiOffsetAndUnit extends React.Component<
    MultiOffsetAndUnitProps,
    MultiMultiOffsetAndUnitState
> {
    constructor(props: MultiOffsetAndUnitProps) {
        super(props);
        const { units, offsets } = props;
        this.state = { units, offsets };
    }

    render = () => {
        const { toPerformLabel } = this.props;

        return (
            <div className={theme.reviewerAllocatedTime}>
                {this.renderOffsetAndUnitInputs()}
                {toPerformLabel}
            </div>
        );
    };

    private renderOffsetAndUnitInputs(): React.ReactNode {
        const { props } = this;
        const { offsets } = this.state;
        switch (props.type) {
            case "EDIT": {
                return (
                    <RawMultiNumericInput
                        type="CREATE"
                        name="offset"
                        values={offsets}
                        min={0}
                        max={this.deriveMaxOffset()}
                        onChange={this.onOffsetChange}
                        className={theme.reviewerAllocatedTimeOffset}
                        addonAfter={this.renderUnitInput()}
                    />
                );
            }
            case "READ_ONLY": {
                return (
                    <RawMultiNumericInput
                        type="READ_ONLY"
                        name="offset"
                        display="Input"
                        values={offsets}
                        className={theme.reviewerAllocatedTimeOffset}
                        addonAfter={this.renderUnitInput()}
                    />
                );
            }
            default: {
                return assertNever(props);
            }
        }
    }

    private renderUnitInput(): React.ReactNode {
        const { props } = this;
        const { units } = this.state;
        switch (props.type) {
            case "EDIT": {
                return (
                    <RawMultiSelectInput
                        type="EDIT"
                        name="unit"
                        values={units}
                        options={[
                            { label: t("ReviewerOffset_Hours"), value: "Hour" },
                            { label: t("ReviewerOffset_Days"), value: "Day" },
                        ]}
                        dropdownMatchSelectWidth={false}
                        className={theme.reviewerAllocatedTimeUnitSelect}
                        onChange={this.onUnitChange}
                    />
                );
            }
            case "READ_ONLY": {
                return (
                    <RawMultiSelectInput
                        type="READ_ONLY"
                        display="Input"
                        name="unit"
                        values={units}
                        options={[
                            { label: t("ReviewerOffset_Hours"), value: "Hour" },
                            { label: t("ReviewerOffset_Days"), value: "Day" },
                        ]}
                        className={theme.reviewerAllocatedTimeUnitSelect}
                    />
                );
            }
            default: {
                return assertNever(props);
            }
        }
    }

    private onUnitChange = (stringUnit: string | undefined) => {
        if (stringUnit === undefined) {
            return;
        }
        const unit = castStringToReviewerOffsetUnit(stringUnit);
        const offset = unwrapOr(this.deriveOffset(), DEFAULT_OFFSET);
        const normalizedOffset = normalizeOffsetForUnit(offset, unit);
        this.handleChange({ unit, offset: normalizedOffset });
    };

    private onOffsetChange = (offset: number | undefined) => {
        if (offset === undefined) {
            return;
        }
        const unit = unwrapOr(this.deriveUnit(), DEFAULT_UNIT);
        const normalizedOffset = normalizeOffsetForUnit(offset, unit);
        this.handleChange({ unit, offset: normalizedOffset });
    };

    private handleChange = (changes: {
        unit: ReviewerOffsetUnit;
        offset: number;
    }) => {
        const { props } = this;
        switch (props.type) {
            case "EDIT": {
                this.setState({
                    units: [changes.unit],
                    offsets: [changes.offset],
                });
                props.onChange(changes);
                break;
            }
            case "READ_ONLY": {
                throw new Error("Attempt to call onChange for READ_ONLY type");
            }
            default: {
                assertNever(props);
            }
        }
    };

    private deriveOffset = (): number | undefined => {
        return deriveSingleValue(this.state.offsets);
    };

    private deriveUnit = (): ReviewerOffsetUnit | undefined => {
        return deriveSingleValue(this.state.units);
    };

    private deriveMaxOffset = (): number => {
        const unit = this.deriveUnit();
        if (unit === undefined) {
            return Math.max(MAX_DAY_OFFSET, MAX_HOUR_OFFSET);
        } else {
            return getMaxOffsetForUnit(unit);
        }
    };
}

function castStringToReviewerOffsetUnit(value: string): ReviewerOffsetUnit {
    switch (value) {
        case ReviewerOffsetUnit.DAY:
        case ReviewerOffsetUnit.HOUR:
            return value;
        default:
            throw new Error(`Cannot cast to ReviewerOffsetUnit: ${value}`);
    }
}

function unwrapOr<T>(value: T | undefined, defaultValue: T): T {
    return value !== undefined ? value : defaultValue;
}

function deriveSingleValue<T>(values: Array<T>): T | undefined {
    if (values.length === 0) {
        return undefined;
    }
    const masterValue = values[0];
    if (values.every((val) => val === masterValue)) {
        return masterValue;
    } else {
        return undefined;
    }
}

function normalizeOffsetForUnit(
    offset: number,
    unit: ReviewerOffsetUnit
): number {
    const max = getMaxOffsetForUnit(unit);
    if (offset < 0) {
        return 0;
    }
    if (offset > max) {
        return max;
    }
    return offset;
}

function getMaxOffsetForUnit(unit: ReviewerOffsetUnit): number {
    switch (unit) {
        case "Hour":
            return MAX_HOUR_OFFSET;
        case "Day":
            return MAX_DAY_OFFSET;
        default:
            return assertNever(unit);
    }
}

export {
    type MultiReviewerAllocatedTimeInputProps,
    type ReviewerAllocatedTime,
    MultiReviewerAllocatedTimeInput,
};
